import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { createplace, getProperties } from "helpers/requests";
import swal from "sweetalert";

const CreatePlace = (props) => {
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState("");
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    max_adults: 1,
    max_children: 0,
    max_additional: 0,
    price: 0,
    property_id: "",
    price_child: 0,
    price_enfant: 0,
    available: true,
  });

  useEffect(() => {
    document.title = "Créer une place";
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const fetched = await getProperties();
        const list = Array.isArray(fetched)
          ? fetched
          : Array.isArray(fetched?.properties)
          ? fetched.properties
          : [];
        setProperties(list);
      } catch (e) {
        console.error("Error fetching properties:", e);
        swal("Erreur", "Impossible de charger les propriétés.", "error");
      } finally {
        setLoadingProperties(false);
      }
    })();
  }, []);

  const selectedProperty = properties.find((p) => p.id === formData.property_id);

  const filteredProperties = properties.filter((p) =>
    `${p.name || ""} ${p.code || ""}`
      .toLowerCase()
      .includes(propertyFilter.toLowerCase())
  );

  const handleSelectProperty = (id) => {
    setFormData((prev) => ({ ...prev, property_id: id }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setNum = (key, delta) =>
    setFormData((prev) => ({
      ...prev,
      [key]: Math.max(0, Number(prev[key] || 0) + delta),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.property_id) {
      return swal("Erreur", "Veuillez sélectionner une propriété.", "error");
    }
    if (!formData.code.trim()) {
      return swal("Erreur", "Le code de la chambre est obligatoire.", "error");
    }
    if (!formData.name.trim()) {
      return swal("Erreur", "Le nom de la place est obligatoire.", "error");
    }
    if (Number(formData.max_adults) < 1) {
      return swal("Erreur", "Le nombre maximum d'adultes doit être au moins 1.", "error");
    }
    if (Number(formData.price) <= 0) {
      return swal("Erreur", "Le prix adulte doit être supérieur à 0.", "error");
    }

    const payload = {
      ...formData,
      max_adults: parseInt(formData.max_adults, 10),
      max_children: parseInt(formData.max_children, 10),
      max_additional: parseInt(formData.max_additional, 10),
      price: parseInt(formData.price, 10),
      price_child: parseInt(formData.price_child, 10),
      price_enfant: parseInt(formData.price_enfant, 10),
    };

    setSubmitting(true);
    try {
      await createplace(payload);
      await swal({
        title: "Succès",
        text: "La place a été créée avec succès.",
        icon: "success",
        button: "OK",
      });
      window.location.href = "/places";
    } catch (error) {
      const dup =
        error?.response?.status === 400 &&
        error?.response?.data?.message === "Place with this code already exists";
      swal(
        "Erreur",
        dup
          ? "Une place avec ce code existe déjà. Veuillez en choisir un autre."
          : "Impossible de créer la place. Veuillez réessayer.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Number stepper helper component ─────────────────────────
  const NumberStepper = ({ name, value, label, suffix, min = 0, helper }) => (
    <FormGroup>
      <Label>{label}</Label>
      <InputGroup>
        <Button
          type="button"
          color="light"
          onClick={() => setFormData((p) => ({ ...p, [name]: Math.max(min, Number(p[name] || 0) - 1) }))}
        >
          <i className="mdi mdi-minus" />
        </Button>
        <Input
          type="number"
          name={name}
          value={value}
          onChange={handleInputChange}
          min={min}
          className="text-center"
        />
        <Button
          type="button"
          color="light"
          onClick={() => setNum(name, 1)}
        >
          <i className="mdi mdi-plus" />
        </Button>
        {suffix && <InputGroupText>{suffix}</InputGroupText>}
      </InputGroup>
      {helper && <small className="text-muted">{helper}</small>}
    </FormGroup>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Places")}
            breadcrumbItem={props.t("Nouvelle place")}
          />

          <Form onSubmit={handleSubmit}>
            {/* ─── 1. Property selector ─────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-home-city-outline text-primary me-2" />
                        Propriété
                      </h5>
                      <p className="text-muted mb-0">
                        Sélectionnez l'établissement auquel cette place appartient.
                      </p>
                    </div>

                    <FormGroup className="mb-3">
                      <InputGroup>
                        <InputGroupText><i className="mdi mdi-magnify" /></InputGroupText>
                        <Input
                          placeholder="Rechercher par nom ou code…"
                          value={propertyFilter}
                          onChange={(e) => setPropertyFilter(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>

                    {loadingProperties ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" /> <span className="ms-2 text-muted">Chargement…</span>
                      </div>
                    ) : filteredProperties.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-home-search-outline display-6 d-block mb-2" />
                        Aucune propriété trouvée.
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-wrap gap-2"
                        style={{ maxHeight: 220, overflowY: "auto" }}
                      >
                        {filteredProperties.map((p) => {
                          const active = p.id === formData.property_id;
                          return (
                            <button
                              type="button"
                              key={p.id}
                              onClick={() => handleSelectProperty(p.id)}
                              className={`border rounded-3 p-2 text-start ${
                                active
                                  ? "border-primary bg-primary-subtle"
                                  : "border-light bg-white"
                              }`}
                              style={{
                                minWidth: 220,
                                cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className={`avatar-xs rounded-circle ${
                                    active ? "bg-primary" : "bg-light"
                                  }`}
                                >
                                  <span
                                    className={`avatar-title rounded-circle ${
                                      active
                                        ? "bg-primary text-white"
                                        : "bg-light text-primary"
                                    }`}
                                  >
                                    <i className="mdi mdi-home-variant" />
                                  </span>
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <strong
                                    className="d-block text-truncate"
                                    style={{ maxWidth: 180 }}
                                  >
                                    {p.name || "—"}
                                  </strong>
                                  <small className="text-muted">
                                    {p.code || "—"}
                                  </small>
                                </div>
                                {active && (
                                  <i className="mdi mdi-check-circle text-primary" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedProperty && (
                      <div className="mt-3 alert alert-success d-flex align-items-center mb-0">
                        <i className="mdi mdi-check-circle me-2" />
                        Propriété sélectionnée : <strong className="ms-1">{selectedProperty.name}</strong>
                        <Badge color="light" className="text-muted ms-2">
                          {selectedProperty.code}
                        </Badge>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── Body only enabled when a property is picked ─── */}
            <fieldset disabled={!formData.property_id} style={{ opacity: formData.property_id ? 1 : 0.5 }}>
              {/* ─── 2. Identity ─────────────────────────────── */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <div className="mb-4">
                        <h5 className="mb-1">
                          <i className="mdi mdi-card-text-outline text-primary me-2" />
                          Identité de la place
                        </h5>
                        <p className="text-muted mb-0">
                          Code interne unique et nom commercial affiché côté client.
                        </p>
                      </div>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Code <span className="text-danger">*</span></Label>
                            <InputGroup>
                              <InputGroupText><i className="mdi mdi-pound" /></InputGroupText>
                              <Input
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                placeholder="VOY-PLC-01"
                              />
                            </InputGroup>
                            <small className="text-muted">
                              Identifiant unique (utilisé en interne).
                            </small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Nom <span className="text-danger">*</span></Label>
                            <InputGroup>
                              <InputGroupText><i className="mdi mdi-tag-outline" /></InputGroupText>
                              <Input
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Chambre triple vue mer"
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* ─── 3. Capacity ─────────────────────────────── */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <div className="mb-4">
                        <h5 className="mb-1">
                          <i className="mdi mdi-account-group-outline text-primary me-2" />
                          Capacité
                        </h5>
                        <p className="text-muted mb-0">
                          Nombre maximum d'occupants autorisés dans la place.
                        </p>
                      </div>
                      <Row>
                        <Col md={4}>
                          <NumberStepper
                            name="max_adults"
                            value={formData.max_adults}
                            label="Adultes max"
                            min={1}
                            helper="Au moins 1 adulte requis."
                          />
                        </Col>
                        <Col md={4}>
                          <NumberStepper
                            name="max_children"
                            value={formData.max_children}
                            label="Enfants max"
                          />
                        </Col>
                        <Col md={4}>
                          <NumberStepper
                            name="max_additional"
                            value={formData.max_additional}
                            label="Personnes additionnelles"
                            helper="Lits d'appoint ou supplémentaires."
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* ─── 4. Pricing ──────────────────────────────── */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <div className="mb-4">
                        <h5 className="mb-1">
                          <i className="mdi mdi-cash-multiple text-primary me-2" />
                          Tarifs
                        </h5>
                        <p className="text-muted mb-0">
                          Prix appliqués par catégorie de voyageur (en dinars algériens).
                        </p>
                      </div>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Prix par adulte <span className="text-danger">*</span></Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="mdi mdi-account text-primary" />
                              </InputGroupText>
                              <Input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                min={0}
                                placeholder="0"
                              />
                              <InputGroupText>DA</InputGroupText>
                            </InputGroup>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Prix par enfant</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="mdi mdi-account-child text-warning" />
                              </InputGroupText>
                              <Input
                                type="number"
                                name="price_child"
                                value={formData.price_child}
                                onChange={handleInputChange}
                                min={0}
                                placeholder="0"
                              />
                              <InputGroupText>DA</InputGroupText>
                            </InputGroup>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Prix par bébé</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="mdi mdi-baby-carriage text-success" />
                              </InputGroupText>
                              <Input
                                type="number"
                                name="price_enfant"
                                value={formData.price_enfant}
                                onChange={handleInputChange}
                                min={0}
                                placeholder="0"
                              />
                              <InputGroupText>DA</InputGroupText>
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Prix de la chambre</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="mdi mdi-bed text-info" />
                              </InputGroupText>
                              <Input
                                type="number"
                                name="max_additional"
                                value={formData.max_additional}
                                onChange={handleInputChange}
                                min={0}
                                placeholder="0"
                              />
                              <InputGroupText>DA</InputGroupText>
                            </InputGroup>
                            <small className="text-muted">
                              Frais fixe associé à la place (le cas échéant).
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* ─── 5. Availability ─────────────────────────── */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <Row className="align-items-center">
                        <Col md={8}>
                          <h5 className="mb-1">
                            <i className="mdi mdi-eye-outline text-primary me-2" />
                            Disponibilité
                          </h5>
                          <p className="text-muted mb-0">
                            Les places indisponibles ne peuvent pas être réservées et n'apparaissent pas dans les voyages.
                          </p>
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                          <div className="form-check form-switch form-switch-md d-inline-block">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="available"
                              checked={formData.available}
                              onChange={() =>
                                setFormData((prev) => ({ ...prev, available: !prev.available }))
                              }
                            />
                            <label className="form-check-label ms-2" htmlFor="available">
                              {formData.available ? (
                                <Badge color="success" className="font-size-12">
                                  <i className="mdi mdi-check me-1" />
                                  Disponible
                                </Badge>
                              ) : (
                                <Badge color="secondary" className="font-size-12">
                                  Indisponible
                                </Badge>
                              )}
                            </label>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </fieldset>

            {/* ─── 6. Submit bar ───────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col md={6}>
                        <small className="text-muted">
                          {!formData.property_id
                            ? "Sélectionnez d'abord une propriété pour activer le formulaire."
                            : "Vérifiez les informations avant de créer la place."}
                        </small>
                      </Col>
                      <Col md={6} className="text-md-end mt-3 mt-md-0">
                        <Button
                          color="light"
                          type="button"
                          onClick={() => (window.location.href = "/places")}
                          disabled={submitting}
                        >
                          Annuler
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={submitting || !formData.property_id}
                          className="ms-2"
                        >
                          {submitting ? (
                            <>
                              <Spinner size="sm" className="me-2" /> Enregistrement…
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-check-bold me-1" />
                              Créer la place
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

CreatePlace.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(CreatePlace);
